import { RetryIcon } from "@solomon-labs/design/components/icons";
import { Button, Text } from "@solomon-labs/design/components/ui";
import React from "react";

export interface BalanceLoadNoticeProps {
    /** The line's text. Omit it for a failure: every load error in the
     *  retail flow reads "Failed to fetch data.", so they look like one
     *  thing. Pass it for a neutral qualifier. Never the raw failure
     *  string, which is the browser's `TypeError` plus an API host. */
    message?: string;
    /** `error` when a value is missing, `neutral` when the value shown
     *  is real but partial (a qualifier, not a failure). */
    tone?: "error" | "neutral";
    onRetry?: () => void;
    /** A retry is in flight: the link reads "Retrying…" and is inert,
     *  so a second click cannot stack requests. */
    retrying?: boolean;
}

/**
 * The line a preview card shows under a value it could not load.
 *
 * A `fine-print` verdict with a `Retry` link beside it. Keeping one shape
 * for every load failure in the retail flow means a user who has seen
 * one knows what the next one is.
 *
 * It sits inside the card whose value failed, not in place of the whole
 * panel, so whatever did load stays on screen. Reachable in
 * `/showcase/balance-errors`.
 */
const FAILED_TO_FETCH = "Failed to fetch data.";

export function BalanceLoadNotice({
    message = FAILED_TO_FETCH,
    tone = "error",
    onRetry,
    retrying = false
}: BalanceLoadNoticeProps): React.ReactElement {
    return (
        <div
            className="flex flex-wrap items-center gap-2"
            role="status"
            aria-live="polite"
        >
            <Text
                variant="fine-print"
                className={
                    tone === "error"
                        ? "text-error-base"
                        : "text-foreground-neutral"
                }
            >
                {message}
            </Text>
            {onRetry && (
                // `2xs` resolves to the `fine-print` Text variant, so the
                // link matches the message beside it.
                <Button
                    variant="link"
                    size="2xs"
                    trailing={retrying ? undefined : <RetryIcon />}
                    onClick={onRetry}
                    disabled={retrying}
                >
                    {retrying ? "Retrying…" : "Retry"}
                </Button>
            )}
        </div>
    );
}
